import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { UtensilsCrossed, ChevronLeft, ChevronRight, Lock, User } from "lucide-react";

const MEAL_LABELS = {
  breakfast: "Petit-déjeuner",
  lunch: "Déjeuner",
  dinner: "Dîner",
  snack1: "Collation 1",
  snack2: "Collation 2",
};

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

export default function CoachMealLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date().toISOString().split("T")[0];
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(searchParams.get("studentId") || "");
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  const month = date.slice(0, 7);

  useEffect(() => {
    api.get("/students").then(r => setStudents(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!studentId) {
      setData(null);
      return;
    }
    setLoading(true);
    setDenied(false);
    api.get(`/meals/coach/${studentId}`, { params: { date, month } })
      .then(r => setData(r.data))
      .catch(err => {
        if (err.response?.status === 403) {
          setDenied(true);
          setData(null);
        } else {
          toast.error("Erreur de chargement");
        }
      })
      .finally(() => setLoading(false));
  }, [studentId, date, month]);

  const handleStudentChange = (id) => {
    setStudentId(id);
    setSearchParams(id ? { studentId: id } : {});
  };

  const totals = useMemo(() => {
    if (!data?.meals) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    return data.meals.reduce((acc, m) => ({
      calories: acc.calories + (m.calories || 0),
      protein: acc.protein + (m.protein || 0),
      carbs: acc.carbs + (m.carbs || 0),
      fat: acc.fat + (m.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  }, [data]);

  const daysInMonth = data?.days_in_month || [];

  return (
    <div data-testid="coach-meal-logs-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center flex-shrink-0">
          <UtensilsCrossed className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Journaux Alimentaires</h1>
          <p className="text-slate-400 mt-1 text-sm">Consultez les repas partagés par vos élèves</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4 items-end">
        <div className="min-w-[220px]">
          <label className="text-xs font-medium text-slate-500 mb-1.5 block">Élève</label>
          <Select value={studentId} onValueChange={handleStudentChange}>
            <SelectTrigger><SelectValue placeholder="Sélectionner un élève" /></SelectTrigger>
            <SelectContent>
              {students.map(s => (
                <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setDate(shiftDate(date, -1))} className="rounded-full">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border border-slate-200 rounded-xl px-3 py-2 text-sm" />
          <Button variant="outline" size="icon" onClick={() => setDate(shiftDate(date, 1))} className="rounded-full">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!studentId ? (
        <div className="text-center py-16 mt-8">
          <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Sélectionnez un élève pour voir son journal</p>
        </div>
      ) : denied ? (
        <Card className="mt-8 border-amber-100 bg-amber-50">
          <CardContent className="p-8 text-center">
            <Lock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="font-medium text-slate-900">Journal non partagé</p>
            <p className="text-sm text-slate-500 mt-1">Cet élève n'a pas activé le partage de son journal alimentaire.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : (
        <>
          {data?.student_name && (
            <p className="mt-4 text-sm text-slate-600">Journal de <strong>{data.student_name}</strong></p>
          )}

          {/* Calendar */}
          <div className="mt-4 flex flex-wrap gap-1.5">
            {Array.from({ length: new Date(parseInt(month.split("-")[0]), parseInt(month.split("-")[1]), 0).getDate() }, (_, i) => {
              const day = String(i + 1).padStart(2, "0");
              const dayStr = `${month}-${day}`;
              const hasEntry = daysInMonth.includes(dayStr);
              const isSelected = dayStr === date;
              return (
                <button
                  key={dayStr}
                  onClick={() => setDate(dayStr)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium ${
                    isSelected ? "bg-green-600 text-white" : hasEntry ? "bg-green-100 text-green-700" : "bg-slate-50 text-slate-400"
                  }`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          <Card className="mt-4 border-green-100">
            <CardContent className="p-4 flex flex-wrap gap-2">
              <Badge>{Math.round(totals.calories)} kcal</Badge>
              <Badge variant="secondary">{totals.protein.toFixed(1)}g prot.</Badge>
              <Badge variant="secondary">{totals.carbs.toFixed(1)}g gluc.</Badge>
              <Badge variant="secondary">{totals.fat.toFixed(1)}g lip.</Badge>
            </CardContent>
          </Card>

          <div className="mt-4 space-y-3">
            {(data?.meals || []).length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-8">Aucun repas enregistré ce jour</p>
            ) : (data?.meals || []).map(meal => (
              <Card key={meal.meal_log_id} className="border-slate-100">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-900">{MEAL_LABELS[meal.meal_type] || meal.meal_type}</h3>
                    {meal.calories > 0 && (
                      <div className="flex gap-1.5">
                        <Badge className="bg-green-50 text-green-700 text-xs">{Math.round(meal.calories)} kcal</Badge>
                        <Badge variant="outline" className="text-xs">{meal.protein}g P · {meal.carbs}g G · {meal.fat}g L</Badge>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{meal.description}</p>
                  {meal.foods?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {meal.foods.map((f, i) => (
                        <span key={i} className="text-xs bg-slate-50 rounded-full px-2 py-0.5">{f.emoji} {f.name}</span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}